import { useState, useEffect, useRef, useMemo } from "react";
import DateInput from "../components/DateInput";

export const NA = "Unavailable";
export const n = (v) => (v === null || v === undefined ? NA : Number(v).toLocaleString("en-US"));
export const fmtDateTime = (iso, tz) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-GB", { timeZone: tz, dateStyle: "medium" });
    const time = d.toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true });
    return `${day}, ${time}`;
  } catch { return new Date(iso).toLocaleString(); }
};
export const fmtTime = (iso, tz) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true }); }
  catch { return iso.slice(11, 16); }
};
export const fmtDay = (iso, tz) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { timeZone: tz, dateStyle: "medium" }); }
  catch { return iso.slice(0, 10); }
};

export const PERIOD_OPTIONS = [
  ["this_month", "This Month"], ["last_month", "Last Month"], ["2", "Last 2 Months"],
  ["3", "Last 3 Months"], ["6", "Last 6 Months"], ["12", "Last 12 Months"], ["custom", "Custom"],
];
const DEFAULT_PERIOD = "last_month";
const MAX_CUSTOM_DAYS = 366;
export const fmtYmd = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { timeZone: "UTC", dateStyle: "medium" }) : "—");
export const fmtRange = (a, b) => (a === b ? fmtYmd(a) : `${fmtYmd(a)} – ${fmtYmd(b)}`);
export function Stat({ value }) {
  return value === null || value === undefined
    ? <span className="metrics-na" title="Meta did not return this value">{NA}</span>
    : n(value);
}

/** Direction of change only: green up, red down, neutral zero, muted when no real baseline exists. */
export function Delta({ change, percent, note }) {
  if (change === null || change === undefined) {
    return <div className="metrics-delta is-na" title={note || ""}>Comparison unavailable</div>;
  }
  const dir = change > 0 ? "up" : change < 0 ? "down" : "zero";
  return (
    <div className={`metrics-delta is-${dir}`} title={note || ""}>
      {dir === "up" && <span aria-hidden="true">▲ </span>}
      {dir === "down" && <span aria-hidden="true">▼ </span>}
      <strong>{change > 0 ? `+${change.toLocaleString("en-US")}` : change < 0 ? `-${Math.abs(change).toLocaleString("en-US")}` : "0"}</strong>
      {percent !== null && percent !== undefined && change !== 0 && <span className="metrics-delta-pct"> ({percent > 0 ? "+" : ""}{percent}%)</span>}
    </div>
  );
}

/** One reporting-period selection (predefined option or applied custom range). */
export function usePeriod(storageKey, fallback = DEFAULT_PERIOD) {
  const [period, setPeriod] = useState(() => {
    if (!storageKey) return fallback; // no storage key: always open on the default, nothing is remembered
    try {
      const v = localStorage.getItem(storageKey);
      return PERIOD_OPTIONS.some(([k]) => k === v) && v !== "custom" ? v : fallback;
    } catch { return fallback; }
  });
  const [custom, setCustom] = useState(null); // applied custom range { from, to }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState({ from: "", to: "" }); // pending, not applied until Apply
  const range = useMemo(() => ({ period, from: custom?.from, to: custom?.to }), [period, custom]);
  const rangeKey = `${period}|${custom?.from || ""}|${custom?.to || ""}`;
  const rangeRef = useRef(range);
  rangeRef.current = range;

  useEffect(() => { try { if (storageKey && period !== "custom") localStorage.setItem(storageKey, period); } catch { /* ignore */ } }, [period, storageKey]);

  const todayStr = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD (the server re-validates)
  // Only the last year of history is kept (one-year retention), so earlier dates are not selectable.
  const minDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toLocaleDateString("en-CA"); })();
  const spanDays = draft.from && draft.to ? Math.round((new Date(draft.to) - new Date(draft.from)) / 864e5) + 1 : 0;
  const draftError =
    !draft.from || !draft.to ? "Select both a From Date and a To Date."
      : draft.to < draft.from ? "The To Date cannot be earlier than the From Date."
        : draft.from < minDate ? `Only the last year of data is available. Choose a From Date on or after ${fmtYmd(minDate)}.`
          : draft.to > todayStr ? "The To Date cannot be in the future."
            : spanDays > MAX_CUSTOM_DAYS ? `The range cannot exceed ${MAX_CUSTOM_DAYS} days.`
              : null;

  const onSelect = (v) => {
    if (v === "custom") {
      setDraft({ from: custom?.from || "", to: custom?.to || "" });
      setPickerOpen(true);
      return;
    }
    setPickerOpen(false);
    setCustom(null); // a predefined filter clears the active custom range
    setPeriod(v);
  };
  const apply = () => {
    if (draftError) return;
    setCustom({ from: draft.from, to: draft.to });
    setPeriod("custom");
    setPickerOpen(false);
  };
  const cancel = () => setPickerOpen(false); // pending edits discarded; applied filter unchanged
  const edit = () => { if (custom) { setDraft({ from: custom.from, to: custom.to }); setPickerOpen(true); } };

  return { period, custom, pickerOpen, draft, setDraft, range, rangeKey, rangeRef, todayStr, minDate, draftError, onSelect, apply, cancel, edit };
}

export function PeriodSelect({ p, label, align, noCustom = false }) {
  return (
    <div className={`metrics-period-wrap${align === "right" ? " is-right" : ""}`}>
      <select className="select-filter" aria-label={label} title={label} value={p.pickerOpen ? "custom" : p.period} onChange={(e) => p.onSelect(e.target.value)}>
        {PERIOD_OPTIONS.filter(([k]) => !(noCustom && k === "custom")).map(([k, text]) => <option key={k} value={k}>{text}</option>)}
      </select>
      {p.pickerOpen && (
        <div className="metrics-picker" role="dialog" aria-label="Custom date range" onKeyDown={(e) => { if (e.key === "Escape") p.cancel(); }}>
          <label className="metrics-picker-field">
            <span>From Date</span>
            <DateInput aria-label="From Date" value={p.draft.from} min={p.minDate} max={p.draft.to || p.todayStr}
              onChange={(e) => p.setDraft((d) => ({ ...d, from: e.target.value }))} />
          </label>
          <label className="metrics-picker-field">
            <span>To Date</span>
            <DateInput aria-label="To Date" value={p.draft.to} min={p.draft.from && p.draft.from > p.minDate ? p.draft.from : p.minDate} max={p.todayStr}
              onChange={(e) => p.setDraft((d) => ({ ...d, to: e.target.value }))} />
          </label>
          {p.draftError && <div className={`metrics-picker-msg ${p.draft.from && p.draft.to ? "is-error" : ""}`}>{p.draftError}</div>}
          <div className="metrics-picker-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={p.cancel}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={p.apply} disabled={!!p.draftError}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
